/**
 * TV Mode Context
 *
 * Provides TV/Kiosk mode functionality for the dashboard:
 * - Auto-rotation between dashboard layouts
 * - Fullscreen API integration
 * - Cursor hiding after inactivity
 * - Scaled fonts for large displays
 * - Progress bar for rotation timer
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';

// ========================================================
// Types
// ========================================================

export interface TVModeConfig {
  /** Whether TV mode is active */
  enabled: boolean;
  /** Hide the toolbar/header */
  hideHeader: boolean;
  /** Auto-rotate between dashboards */
  autoRotate: boolean;
  /** Rotation interval in seconds (default: 45) */
  rotationInterval: number;
  /** Font scale multiplier (1.0 = normal, 1.5 = 150%) */
  scale: number;
  /** Hide cursor after inactivity (seconds, 0 = never) */
  cursorHideDelay: number;
  /** Show rotation progress bar */
  showProgressBar: boolean;
  /** Show page indicator dots */
  showPageDots: boolean;
}

interface TVModeState {
  config: TVModeConfig;
  /** Progress of current rotation (0-100) */
  rotationProgress: number;
  /** Index of current layout in rotation */
  currentRotationIndex: number;
  /** Whether cursor is currently hidden */
  isCursorHidden: boolean;
  /** Whether we're in fullscreen */
  isFullscreen: boolean;
}

interface TVModeContextValue {
  state: TVModeState;
  /** Enter TV mode with optional config overrides */
  enterTVMode: (config?: Partial<TVModeConfig>) => void;
  /** Exit TV mode */
  exitTVMode: () => void;
  /** Update TV mode config */
  updateConfig: (config: Partial<TVModeConfig>) => void;
  /** Toggle TV mode on/off */
  toggleTVMode: () => void;
  /** Pause auto-rotation */
  pauseRotation: () => void;
  /** Resume auto-rotation */
  resumeRotation: () => void;
}

// ========================================================
// Default config
// ========================================================

const DEFAULT_TV_CONFIG: TVModeConfig = {
  enabled: false,
  hideHeader: true,
  autoRotate: true,
  rotationInterval: 45,
  scale: 1.3, // Extra zoom on top of auto-scale for TV/kiosk displays
  cursorHideDelay: 3,
  showProgressBar: true,
  showPageDots: true,
};

// ========================================================
// Context
// ========================================================

const TVModeContext = createContext<TVModeContextValue | undefined>(undefined);

// ========================================================
// Provider
// ========================================================

interface TVModeProviderProps {
  children: ReactNode;
  /** Callback when rotation triggers - receives next layout index */
  onRotate?: (nextIndex: number) => void;
  /** Total number of layouts available for rotation */
  layoutCount?: number;
}

export const TVModeProvider: React.FC<TVModeProviderProps> = ({
  children,
  onRotate,
  layoutCount = 1,
}) => {
  const [state, setState] = useState<TVModeState>({
    config: DEFAULT_TV_CONFIG,
    rotationProgress: 0,
    currentRotationIndex: 0,
    isCursorHidden: false,
    isFullscreen: false,
  });

  const rotationTimerRef = useRef<number | null>(null);
  const progressTimerRef = useRef<number | null>(null);
  const cursorTimerRef = useRef<number | null>(null);
  const isPausedRef = useRef(false);

  // ========================================================
  // Fullscreen management
  // ========================================================

  const enterFullscreen = useCallback(() => {
    document.documentElement.requestFullscreen?.().catch(() => {
      // Fullscreen may be blocked by browser policy
    });
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  // Track fullscreen state changes
  useEffect(() => {
    const handler = () => {
      setState((prev) => ({
        ...prev,
        isFullscreen: !!document.fullscreenElement,
      }));
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // ========================================================
  // Cursor hiding
  // ========================================================

  const resetCursorTimer = useCallback(() => {
    if (!state.config.enabled || state.config.cursorHideDelay <= 0) return;

    // Show cursor
    setState((prev) => ({ ...prev, isCursorHidden: false }));
    document.body.style.cursor = '';

    // Clear existing timer
    if (cursorTimerRef.current) {
      window.clearTimeout(cursorTimerRef.current);
    }

    // Set new timer to hide cursor
    cursorTimerRef.current = window.setTimeout(() => {
      setState((prev) => ({ ...prev, isCursorHidden: true }));
      document.body.style.cursor = 'none';
    }, state.config.cursorHideDelay * 1000);
  }, [state.config.enabled, state.config.cursorHideDelay]);

  useEffect(() => {
    if (!state.config.enabled) return;

    const handleMouseMove = () => resetCursorTimer();
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (cursorTimerRef.current) {
        window.clearTimeout(cursorTimerRef.current);
      }
      document.body.style.cursor = '';
    };
  }, [state.config.enabled, resetCursorTimer]);

  // ========================================================
  // Auto-rotation
  // ========================================================

  useEffect(() => {
    if (!state.config.enabled || !state.config.autoRotate || layoutCount <= 1) {
      // Clear timers if not rotating
      if (rotationTimerRef.current) window.clearInterval(rotationTimerRef.current);
      if (progressTimerRef.current) window.clearInterval(progressTimerRef.current);
      return;
    }

    const intervalMs = state.config.rotationInterval * 1000;
    const progressStep = 100 / (intervalMs / 100); // Update every 100ms

    // Progress timer (updates progress bar)
    progressTimerRef.current = window.setInterval(() => {
      if (isPausedRef.current) return;

      setState((prev) => {
        const newProgress = prev.rotationProgress + progressStep;

        if (newProgress >= 100) {
          // Time to rotate
          const nextIndex = (prev.currentRotationIndex + 1) % layoutCount;
          onRotate?.(nextIndex);

          return {
            ...prev,
            rotationProgress: 0,
            currentRotationIndex: nextIndex,
          };
        }

        return {
          ...prev,
          rotationProgress: newProgress,
        };
      });
    }, 100);

    return () => {
      if (rotationTimerRef.current) window.clearInterval(rotationTimerRef.current);
      if (progressTimerRef.current) window.clearInterval(progressTimerRef.current);
    };
  }, [
    state.config.enabled,
    state.config.autoRotate,
    state.config.rotationInterval,
    layoutCount,
    onRotate,
  ]);

  // ========================================================
  // Public API
  // ========================================================

  const enterTVMode = useCallback((configOverrides?: Partial<TVModeConfig>) => {
    setState((prev) => ({
      ...prev,
      config: {
        ...DEFAULT_TV_CONFIG,
        ...configOverrides,
        enabled: true,
      },
      rotationProgress: 0,
      currentRotationIndex: 0,
    }));
    enterFullscreen();
  }, [enterFullscreen]);

  const exitTVMode = useCallback(() => {
    setState((prev) => ({
      ...prev,
      config: { ...prev.config, enabled: false },
      rotationProgress: 0,
      isCursorHidden: false,
    }));
    exitFullscreen();
    document.body.style.cursor = '';

    // Clear all timers
    if (rotationTimerRef.current) window.clearInterval(rotationTimerRef.current);
    if (progressTimerRef.current) window.clearInterval(progressTimerRef.current);
    if (cursorTimerRef.current) window.clearTimeout(cursorTimerRef.current);
  }, [exitFullscreen]);

  const toggleTVMode = useCallback(() => {
    if (state.config.enabled) {
      exitTVMode();
    } else {
      enterTVMode();
    }
  }, [state.config.enabled, enterTVMode, exitTVMode]);

  const updateConfig = useCallback((configUpdates: Partial<TVModeConfig>) => {
    setState((prev) => ({
      ...prev,
      config: { ...prev.config, ...configUpdates },
    }));
  }, []);

  const pauseRotation = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  const resumeRotation = useCallback(() => {
    isPausedRef.current = false;
  }, []);

  // ========================================================
  // Keyboard shortcuts
  // ========================================================

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC to exit TV mode
      if (e.key === 'Escape' && state.config.enabled) {
        exitTVMode();
      }

      // F11 to toggle TV mode
      if (e.key === 'F11') {
        e.preventDefault();
        toggleTVMode();
      }

      // Space to pause/resume rotation
      if (e.key === ' ' && state.config.enabled) {
        e.preventDefault();
        if (isPausedRef.current) {
          resumeRotation();
        } else {
          pauseRotation();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.config.enabled, exitTVMode, toggleTVMode, pauseRotation, resumeRotation]);

  const value: TVModeContextValue = {
    state,
    enterTVMode,
    exitTVMode,
    updateConfig,
    toggleTVMode,
    pauseRotation,
    resumeRotation,
  };

  return <TVModeContext.Provider value={value}>{children}</TVModeContext.Provider>;
};

// ========================================================
// Hook
// ========================================================

export const useTVMode = (): TVModeContextValue => {
  const context = useContext(TVModeContext);
  if (!context) {
    throw new Error('useTVMode must be used within a TVModeProvider');
  }
  return context;
};

export default TVModeContext;
